import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { api } from '../api/client';
import { setAuthExpiryHandler } from '../api/global-error-handler';
import { queryClient } from '../query/QueryProvider';
import type { CurrentUser, MeResponse } from '@myscaw/shared';

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone: string;
    gender: 'MALE' | 'FEMALE';
    acceptsPrivacyPolicy: true;
  }) => Promise<void>;
  verify: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * `GET /auth/me` answers `MeResponse` - `{ user, roles, permissions }`, doc 18
   * section 3.2 - and the context holds the flattened `CurrentUser`.
   *
   * This used to read `api.get<CurrentUser>('/auth/me')` and store the response
   * as-is. TypeScript believed it, because the type argument is an assertion
   * rather than a check, and the result was a `user` object whose `roles` and
   * `permissions` happened to line up while `userId`, `email` and the name were
   * silently `undefined` on every screen that read them.
   */
  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setUser({
        userId: me.user.id,
        email: me.user.email,
        firstName: me.user.firstName,
        lastName: me.user.lastName,
        avatarUrl: me.user.avatarUrl,
        roles: me.roles,
        permissions: me.permissions,
      });
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // A 401 from any protected call while "we believe we are signed in" means the
  // session died server-side: drop the cached user and empty the query cache.
  useEffect(() => {
    setAuthExpiryHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setAuthExpiryHandler(undefined);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await api.post('/auth/login', { email, password });
      await refresh();
    },
    [refresh],
  );

  const signUp = useCallback(
    async (data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phone: string;
      gender: 'MALE' | 'FEMALE';
      acceptsPrivacyPolicy: true;
    }) => {
      await api.post('/auth/signup', data);
    },
    [],
  );

  const verify = useCallback(
    async (email: string, code: string) => {
      await api.post('/auth/verify', { email, code });
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, verify, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
